import axios from 'axios'
import { attachAuth } from '../../lib/apiAuth'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8899'

const api = axios.create({
  baseURL: `${API_BASE}/admin/indicators`,
  headers: { 'Content-Type': 'application/json' },
})

// --- Types ---

export interface IndicatorAdminInfo {
  id: number
  code: string
  name: string
  unit: string | null
  category: string | null
  subcategory: string | null
  source: string | null
  is_visible: boolean
  sort_order: number
  is_calculated: boolean
}

export interface IndicatorAdminListResponse {
  items: IndicatorAdminInfo[]
  total: number
}

export interface CalculatedIndicatorResponse {
  id: number
  indicator_id: number
  name: string
  description: string | null
  unit: string | null
  category: string | null
  subcategory: string | null
  formula_type: string
  formula_definition: Record<string, unknown>
  text_mapping: Record<string, unknown> | null
  entity_types: string[]
  is_visible: boolean
  created_at: string
  updated_at: string
}

export interface CalculatedIndicatorCreate {
  name: string
  description?: string
  unit?: string
  category?: string
  subcategory?: string
  formula_type: 'arithmetic' | 'quantile_rank' | 'weighted_rank' | 'weighted_composite'
  formula_definition: Record<string, unknown>
  text_mapping?: Record<string, unknown>
  entity_types: string[]
}

export interface PreviewResult {
  entity_id: number
  entity_name: string
  entity_ags: string
  score: number | null
  text_label: string | null
}

export interface PreviewResponse {
  results: PreviewResult[]
  entity_type: string
  errors: string[]
}

// --- API Methods ---

export async function getIndicators(params?: {
  source?: string
  category?: string
  is_visible?: boolean
  search?: string
  limit?: number
  offset?: number
}): Promise<IndicatorAdminListResponse> {
  const response = await api.get<IndicatorAdminListResponse>('', { params })
  return response.data
}

export async function updateVisibility(indicatorId: number, isVisible: boolean) {
  const response = await api.patch(`/${indicatorId}/visibility`, { is_visible: isVisible })
  return response.data
}

export async function updateVisibilityBulk(indicatorIds: number[], isVisible: boolean) {
  const response = await api.patch('/visibility/bulk', {
    indicator_ids: indicatorIds,
    is_visible: isVisible,
  })
  return response.data
}

export async function getCalculatedIndicators(): Promise<{ items: CalculatedIndicatorResponse[]; total: number }> {
  const response = await api.get('/calculated')
  return response.data
}

export async function createCalculatedIndicator(body: CalculatedIndicatorCreate): Promise<CalculatedIndicatorResponse> {
  const response = await api.post('/calculated', body)
  return response.data
}

export async function updateCalculatedIndicator(
  id: number,
  body: Partial<CalculatedIndicatorCreate>
): Promise<CalculatedIndicatorResponse> {
  const response = await api.put(`/calculated/${id}`, body)
  return response.data
}

export async function deleteCalculatedIndicator(id: number) {
  const response = await api.delete(`/calculated/${id}`)
  return response.data
}

export async function previewCalculatedIndicator(
  id: number,
  entityType: string,
  limit = 5
): Promise<PreviewResponse> {
  const response = await api.post(`/calculated/${id}/preview`, {
    entity_type: entityType,
    limit,
  })
  return response.data
}

// Basic-Auth-Header + 401-Behandlung (optionaler Schutz, v0.3.8)
attachAuth(api)
